import mixpanel, { type Config } from 'mixpanel-browser';
import shortHash from 'shorthash2';

import { isStatisticsEnabled } from '../utils/cookiebot';
import { AnalyticsEvents } from '../utils/customevents';
import { subscribeToPageChange } from '../utils/subscribeToPageChange';
import type { AnalyticsInterface } from './AnalyticsInterface';

interface AnalyticsOptions {
  isProduction: boolean;
}

interface TrackEventsOptions {
  flush?: boolean;
}

export class MixpanelAnalytics<E extends string, T extends Record<string, unknown>> implements AnalyticsInterface<E, T> {
  private isInitialized = false;
  private isAutomaticPageViewEnabled = false;

  constructor(
    private readonly token: string,
    private readonly analyticsOptions: AnalyticsOptions,
    private readonly mixpanelConfig: Partial<Config> = {}
  ) {}

  init() {
    mixpanel.init(this.token, {
      api_host: 'https://api-eu.mixpanel.com',
      ignore_dnt: true,
      debug: !this.analyticsOptions.isProduction,
      verbose: !this.analyticsOptions.isProduction,
      disable_persistence: true,
      track_pageview: false,
      record_mask_text_selector: '.sensitive-string',
      record_sessions_percent: this.analyticsOptions.isProduction ? 0 : 100,
      ...this.mixpanelConfig,
    });

    this.isInitialized = true;
  }

  trackEvent(name: E, properties?: T, { flush = false }: TrackEventsOptions = {}) {
    if (!this.isInitialized && !isStatisticsEnabled(this.analyticsOptions.isProduction)) {
      return;
    }

    mixpanel.track(name, properties, { send_immediately: flush });
  }

  trackButtonClick(properties?: T) {
    this.trackEvent(AnalyticsEvents.CLICK_BUTTON as E, properties);
  }

  trackPageView(properties?: T) {
    if (!this.isInitialized || !isStatisticsEnabled(this.analyticsOptions.isProduction)) {
      return;
    }

    mixpanel.track_pageview(properties);
  }

  setPropertiesForAllEvents(properties: T) {
    mixpanel.register(properties);
  }

  identify(email: string, isKeyUser = false) {
    const domain = email.split('@')[1];
    const userId = `${shortHash(email)} (*****@${domain})`;

    mixpanel.identify(userId);
    mixpanel.people.set({ isKeyUser });
  }

  enableAutomaticPageView() {
    if (!this.isAutomaticPageViewEnabled) {
      subscribeToPageChange(() => this.trackPageView());
      this.isAutomaticPageViewEnabled = true;
    }
  }
}
